import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, Field, Icon, Input, toast } from "@sagot/ui";
import { useState } from "react";
import { useNavigate } from "react-router";
import { useErrorMessage } from "../../../../lib/api-errors";
import { useWorkspaceContext } from "../../../../session/workspace-context";
import { useDuplicateWorkflow } from "./use-defaults";

const D = "engage.workflows.defaults.duplicate";
const NAME_MAX = 120;

interface DuplicateDialogProps {
  workflow: { id: string; name: string };
  onClose: () => void;
  onCloseAutoFocus?: (event: Event) => void;
}

/** "Duplicate": a workflow of the workspace's own made from a default. The default keeps running, and the dialog says so, so nobody sends twice by accident. */
export function DuplicateDialog({ workflow, onClose, onCloseAutoFocus }: DuplicateDialogProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const navigate = useNavigate();
  const { summary } = useWorkspaceContext();
  const duplicate = useDuplicateWorkflow(workflow.id);
  const [name, setName] = useState(() => t(`${D}.copyName`, { name: workflow.name }).slice(0, NAME_MAX));
  const submit = () => {
    duplicate.mutate({ name: name.trim() || undefined }, {
      onSuccess: (copy) => {
        toast.success(t(`${D}.created`));
        onClose();
        void navigate(consoleRoutes.workflow(summary.slug, copy.id));
      },
      onError: (error) => { toast.error(message(error)); },
    });
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        title={t(`${D}.title`, { name: workflow.name })}
        description={t(`${D}.body`)}
        closeLabel={t("common.close")}
        onCloseAutoFocus={onCloseAutoFocus}
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
            <Button loading={duplicate.isPending} onClick={submit}>
              <Icon name="copy" />
              {t(`${D}.confirm`)}
            </Button>
          </>
        }
      >
        <form className="space-y-3" onSubmit={(event) => { event.preventDefault(); submit(); }}>
          <Field label={t(`${D}.name`)}>
            <Input maxLength={NAME_MAX} value={name} onChange={(event) => { setName(event.target.value); }} />
          </Field>
          <p className="text-xs text-fg-subtle">{t(`${D}.swapped`)}</p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
